import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { abTestApi } from 'api/endpoints/ab_test.api';
import {
  ABTest,
  ABTestCreateRequest,
  ABTestListItem,
  ABTestReport,
  ABTestSendJob,
  ABTestStatus,
  ABMetric,
} from 'types/ab_test.types';

interface ABTestState {
  tests: ABTestListItem[];
  total: number;
  page: number;
  pageSize: number;
  statusFilter?: string;

  currentTest: ABTest | null;
  report: ABTestReport | null;
  metric: ABMetric;

  sendJob: ABTestSendJob | null;

  isLoading: boolean;
  isReportLoading: boolean;
  isSending: boolean;
  error: string | null;
}

const initialState: ABTestState = {
  tests: [],
  total: 0,
  page: 1,
  pageSize: 20,
  statusFilter: undefined,

  currentTest: null,
  report: null,
  metric: 'ctr',

  sendJob: null,

  isLoading: false,
  isReportLoading: false,
  isSending: false,
  error: null,
};

const errMsg = (e: any, fallback: string) =>
  e?.response?.data?.error || e?.message || fallback;

export const fetchABTests = createAsyncThunk(
  'abTest/fetchAll',
  async (
    args: { appId: number; status?: string; limit?: number; offset?: number },
    { rejectWithValue }
  ) => {
    try {
      return await abTestApi.list(args.appId, args.status, args.limit ?? 20, args.offset ?? 0);
    } catch (e: any) {
      return rejectWithValue(errMsg(e, 'Не удалось загрузить список тестов'));
    }
  }
);

export const fetchABTest = createAsyncThunk(
  'abTest/fetchOne',
  async (id: number, { rejectWithValue }) => {
    try {
      return await abTestApi.get(id);
    } catch (e: any) {
      return rejectWithValue(errMsg(e, 'Тест не найден'));
    }
  }
);

export const fetchABTestReport = createAsyncThunk(
  'abTest/fetchReport',
  async ({ id, metric }: { id: number; metric: ABMetric }, { rejectWithValue }) => {
    try {
      return await abTestApi.getResults(id, metric);
    } catch (e: any) {
      return rejectWithValue(errMsg(e, 'Не удалось загрузить результаты'));
    }
  }
);

export const createABTest = createAsyncThunk(
  'abTest/create',
  async (data: ABTestCreateRequest, { rejectWithValue }) => {
    try {
      return await abTestApi.create(data);
    } catch (e: any) {
      return rejectWithValue(errMsg(e, 'Не удалось создать тест'));
    }
  }
);

export const updateABTest = createAsyncThunk(
  'abTest/update',
  async ({ id, data }: { id: number; data: ABTestCreateRequest }, { rejectWithValue }) => {
    try {
      await abTestApi.update(id, data);
      return id;
    } catch (e: any) {
      return rejectWithValue(errMsg(e, 'Не удалось сохранить тест'));
    }
  }
);

export const duplicateABTest = createAsyncThunk(
  'abTest/duplicate',
  async ({ id, name }: { id: number; name?: string }, { rejectWithValue }) => {
    try {
      return await abTestApi.duplicate(id, name);
    } catch (e: any) {
      return rejectWithValue(errMsg(e, 'Не удалось создать копию'));
    }
  }
);

export const deleteABTest = createAsyncThunk(
  'abTest/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await abTestApi.delete(id);
      return id;
    } catch (e: any) {
      return rejectWithValue(errMsg(e, 'Не удалось удалить тест'));
    }
  }
);

export const startABTest = createAsyncThunk(
  'abTest/start',
  async (id: number, { rejectWithValue }) => {
    try {
      await abTestApi.start(id);
      return id;
    } catch (e: any) {
      return rejectWithValue(errMsg(e, 'Не удалось запустить тест'));
    }
  }
);

export const pauseABTest = createAsyncThunk(
  'abTest/pause',
  async (id: number, { rejectWithValue }) => {
    try {
      await abTestApi.pause(id);
      return id;
    } catch (e: any) {
      return rejectWithValue(errMsg(e, 'Не удалось поставить тест на паузу'));
    }
  }
);

export const completeABTest = createAsyncThunk(
  'abTest/complete',
  async (
    { id, winnerVariantId, metric }: { id: number; winnerVariantId: number; metric?: ABMetric },
    { rejectWithValue }
  ) => {
    try {
      await abTestApi.complete(id, winnerVariantId, metric ?? 'ctr');
      return id;
    } catch (e: any) {
      return rejectWithValue(errMsg(e, 'Не удалось завершить тест'));
    }
  }
);

export const sendABTestPush = createAsyncThunk(
  'abTest/send',
  async (id: number, { rejectWithValue }) => {
    try {
      const res = await abTestApi.send(id);
      return res.job;
    } catch (e: any) {
      return rejectWithValue(errMsg(e, 'Не удалось запустить рассылку'));
    }
  }
);

export const fetchSendStatus = createAsyncThunk(
  'abTest/sendStatus',
  async (id: number) => abTestApi.sendStatus(id)
);

const setStatus = (state: ABTestState, id: number, status: ABTestStatus) => {
  const test = state.tests.find((t) => t.id === id);
  if (test) test.status = status;
  if (state.currentTest?.id === id) state.currentTest.status = status;
  if (state.report?.test_id === id) state.report.status = status;
};

const abTestSlice = createSlice({
  name: 'abTest',
  initialState,
  reducers: {
    setMetric: (state, action: PayloadAction<ABMetric>) => {
      state.metric = action.payload;
    },
    setPage: (state, action: PayloadAction<{ page: number; pageSize: number }>) => {
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
    },
    setStatusFilter: (state, action: PayloadAction<string | undefined>) => {
      state.statusFilter = action.payload;
      state.page = 1;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearTests: (state) => {
      state.tests = [];
      state.total = 0;
      state.page = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchABTests.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchABTests.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tests = action.payload.tests || [];
        state.total = action.payload.total || 0;
      })
      .addCase(fetchABTests.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Не удалось загрузить список тестов';
      })

      .addCase(fetchABTest.fulfilled, (state, action) => {
        state.currentTest = action.payload;
      })

      .addCase(fetchABTestReport.pending, (state) => {
        state.isReportLoading = true;
      })
      .addCase(fetchABTestReport.fulfilled, (state, action) => {
        state.isReportLoading = false;
        state.report = action.payload;
      })
      .addCase(fetchABTestReport.rejected, (state, action) => {
        state.isReportLoading = false;
        state.error = (action.payload as string) || 'Не удалось загрузить результаты';
      })

      .addCase(createABTest.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createABTest.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(createABTest.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Не удалось создать тест';
      })

      .addCase(deleteABTest.fulfilled, (state, action) => {
        state.tests = state.tests.filter((t) => t.id !== action.payload);
        state.total = Math.max(0, state.total - 1);
        if (state.currentTest?.id === action.payload) state.currentTest = null;
      })

      .addCase(startABTest.fulfilled, (state, action) => {
        setStatus(state, action.payload, ABTestStatus.RUNNING);
      })
      .addCase(pauseABTest.fulfilled, (state, action) => {
        setStatus(state, action.payload, ABTestStatus.PAUSED);
      })
      .addCase(completeABTest.fulfilled, (state, action) => {
        setStatus(state, action.payload, ABTestStatus.COMPLETED);
      })

      .addCase(sendABTestPush.pending, (state) => {
        state.isSending = true;
        state.error = null;
      })
      .addCase(sendABTestPush.fulfilled, (state, action) => {
        state.isSending = false;
        state.sendJob = action.payload;
      })
      .addCase(sendABTestPush.rejected, (state, action) => {
        state.isSending = false;
        state.error = (action.payload as string) || 'Не удалось запустить рассылку';
      })

      .addCase(fetchSendStatus.fulfilled, (state, action) => {
        state.sendJob = action.payload;
      });
  },
});

export const { setMetric, setPage, setStatusFilter, clearError, clearTests } =
  abTestSlice.actions;

export const selectABTests = (s: { abTest: ABTestState }) => s.abTest.tests;
export const selectABTestsTotal = (s: { abTest: ABTestState }) => s.abTest.total;
export const selectABTestPage = (s: { abTest: ABTestState }) => s.abTest.page;
export const selectABTestPageSize = (s: { abTest: ABTestState }) => s.abTest.pageSize;
export const selectABTestStatusFilter = (s: { abTest: ABTestState }) => s.abTest.statusFilter;
export const selectCurrentABTest = (s: { abTest: ABTestState }) => s.abTest.currentTest;
export const selectABTestReport = (s: { abTest: ABTestState }) => s.abTest.report;
export const selectABTestMetric = (s: { abTest: ABTestState }) => s.abTest.metric;
export const selectABTestSendJob = (s: { abTest: ABTestState }) => s.abTest.sendJob;
export const selectABTestLoading = (s: { abTest: ABTestState }) => s.abTest.isLoading;
export const selectABTestReportLoading = (s: { abTest: ABTestState }) => s.abTest.isReportLoading;
export const selectABTestSending = (s: { abTest: ABTestState }) => s.abTest.isSending;
export const selectABTestError = (s: { abTest: ABTestState }) => s.abTest.error;

export default abTestSlice.reducer;